import { CalculateDynamicPriceImpact } from '@ostium/formulae';
import {
  formatUsdc,
  formatTokens,
  parseUsdc,
  parseTokens,
  parseLeverage,
  timeElapsedSince,
} from './decimal.js';
import type { RawPair } from './raw.js';
import { normalizePairName } from './pair-names.js';
import type {
  PriceData,
  SimSlippageForPair,
  SimSlippageRow,
  SimOrderbookLevel,
  SimOrderbookResponse,
} from '../types.js';

const EMPTY_RESULT: SimSlippageForPair = { long: [], short: [] };

/** Max levels-per-side cap exposed by `getSimOrderbook`. */
const ORDERBOOK_MAX_LEVELS = 20;

/**
 * Build log-spaced notional levels from ~capacity/3300 up to capacity.
 * The last level snaps to exactly `capacity` so the orderbook always shows the
 * full remaining open-interest as its deepest level.
 *
 * Example with 8 levels and $3.3M capacity → ≈ $1k, $10k, $50k, $100k, $250k, $1M, $2.5M, $3.3M
 */
function generateLogSpacedNotionals(levels: number, capacity: number): number[] {
  const n = Math.min(ORDERBOOK_MAX_LEVELS, Math.max(1, Math.floor(levels)));
  if (capacity <= 0) return [];
  if (n === 1) return [capacity];

  const minNtl = Math.max(1, capacity / 3300);
  if (minNtl >= capacity) return [capacity];

  const logMin = Math.log(minNtl);
  const logMax = Math.log(capacity);

  return Array.from({ length: n }, (_, i) => {
    if (i === n - 1) return capacity;
    const t = i / (n - 1);
    return Math.exp(logMin + t * (logMax - logMin));
  });
}

/** Side capacities + bid/mid/ask BigNumber strings + cached time-elapsed for one pair. */
interface PairContext {
  remLong: number;
  remShort: number;
  midBN: string;
  askBN: string;
  bidBN: string;
  timeElapsed: string;
}

function buildPairContext(raw: RawPair, price: PriceData): PairContext {
  const maxOIUsd   = formatUsdc(raw.maxOI);
  const longOIUsd  = formatTokens(raw.longOI)  * price.mid;
  const shortOIUsd = formatTokens(raw.shortOI) * price.mid;
  return {
    remLong:     Math.max(0, maxOIUsd - longOIUsd),
    remShort:    Math.max(0, maxOIUsd - shortOIUsd),
    midBN:       parseTokens(price.mid).toString(),
    askBN:       parseTokens(price.ask).toString(),
    bidBN:       parseTokens(price.bid).toString(),
    timeElapsed: timeElapsedSince(raw),
  };
}

/** Run `CalculateDynamicPriceImpact` for one (pair, side, ntl) tuple. */
function priceImpactForLevel(
  raw: RawPair,
  ctx: PairContext,
  ntl: number,
  isBuy: boolean,
): { priceImpactP: string; priceAfterImpact: string } {
  return CalculateDynamicPriceImpact(
    ctx.midBN,
    ctx.askBN,
    ctx.bidBN,
    true,    // isOpen
    isBuy,
    parseUsdc(ntl).toString(),
    parseLeverage(1).toString(),
    raw.buyVolume       ?? '0',
    raw.sellVolume      ?? '0',
    raw.decayRate       ?? '0',
    ctx.timeElapsed,
    raw.netVolThreshold ?? '0',
    raw.priceImpactK    ?? '0',
  );
}

/**
 * Simulate the dynamic-spread slippage a long and a short trade of each
 * notional would experience on the given pair. Notionals exceeding the side's
 * remaining open-interest capacity (`maxOI − sideOI`) are dropped.
 *
 * Slippage is the per-side `priceImpactP` returned by `CalculateDynamicPriceImpact`
 * — the same formula the contract and frontend use — formatted as a percentage
 * (e.g. `"0.5"` = 0.5 %).
 *
 * @param liveMid  Optional override for `price.mid`. Falls back to `price.mid`.
 */
export function simulateSlippageForPair(
  raw: RawPair,
  ntls: string[],
  price: PriceData | undefined,
): SimSlippageForPair {
  if (!price || !price.mid) return EMPTY_RESULT;
  const ctx = buildPairContext(raw, price);

  const compute = (isBuy: boolean, capacity: number): SimSlippageRow[] =>
    ntls
      .filter(ntl => {
        const n = Number(ntl);
        return Number.isFinite(n) && n > 0 && n <= capacity;
      })
      .map(ntl => {
        const result = priceImpactForLevel(raw, ctx, Number(ntl), isBuy);
        return { ntl, slippage: formatTokens(result.priceImpactP).toString() };
      });

  return {
    long:  compute(true,  ctx.remLong),
    short: compute(false, ctx.remShort),
  };
}

/**
 * Build a simulated bid/ask orderbook for one pair using log-spaced notionals
 * from ~capacity/3300 up to the side's full remaining open-interest capacity.
 * `levels` is clamped to `[1, 20]`.
 *
 * - `asks` model long entries — `priceAfterImpact` is the price you'd buy at.
 * - `bids` model short entries — `priceAfterImpact` is the price you'd sell at.
 */
export function simulateOrderbookForPair(
  raw: RawPair,
  price: PriceData,
  levels: number,
): SimOrderbookResponse {
  const safeLevels = Math.min(ORDERBOOK_MAX_LEVELS, Math.max(1, Math.floor(levels)));
  const ctx        = buildPairContext(raw, price);

  const askNtls = generateLogSpacedNotionals(safeLevels, ctx.remLong);
  const bidNtls = generateLogSpacedNotionals(safeLevels, ctx.remShort);

  const toLevel = (ntl: number, isBuy: boolean): SimOrderbookLevel => {
    const result = priceImpactForLevel(raw, ctx, ntl, isBuy);
    const px = formatTokens(result.priceAfterImpact);
    return {
      px: px.toString(),
      sz: (px > 0 ? ntl / px : 0).toString(),
      n:  1,
    };
  };

  return {
    pairId:   String(raw.id),
    pairFrom: normalizePairName(raw.from),
    pairTo:   normalizePairName(raw.to),
    levels: [
      bidNtls.map(n => toLevel(n, false)),  // bids: short entries, best (closest to mid) first
      askNtls.map(n => toLevel(n, true)),   // asks: long entries, best (closest to mid) first
    ],
    time: Date.now(),
  };
}
